/**
 * Export loadData calls from ducks modules of different containers
 */
import { loadData as AuthenticationPageLoader } from './AuthenticationPage/AuthenticationPage.duck';
import { loadData as LandingPageLoader } from './LandingPage/LandingPage.duck';
import { setInitialValues as CheckoutPageInitialValues } from './CheckoutPage/CheckoutPage.duck';
import { loadData as CMSPageLoader } from './CMSPage/CMSPage.duck';
import { loadData as ContactDetailsPageLoader } from './ContactDetailsPage/ContactDetailsPage.duck';
import { loadData as EditListingPageLoader } from './EditListingPage/EditListingPage.duck';
import { loadData as EmailVerificationPageLoader } from './EmailVerificationPage/EmailVerificationPage.duck';
import { loadData as GroupPageLoader } from './GroupsPage/GroupPage.duck';
import { loadData as GroupsPageLoader } from './GroupsPage/GroupsPage.duck';
import { loadData as InboxPageLoader } from './InboxPage/InboxPage.duck';
import { loadData as ListingPageLoader } from './ListingPage/ListingPage.duck';
import { loadData as ManageListingsPageLoader } from './ManageListingsPage/ManageListingsPage.duck';
import { loadData as PaymentMethodsPageLoader } from './PaymentMethodsPage/PaymentMethodsPage.duck';
import { loadData as PrivacyPolicyPageLoader } from './PrivacyPolicyPage/PrivacyPolicyPage.duck';
import { loadData as ProfilePageLoader } from './ProfilePage/ProfilePage.duck';
import { loadData as StorePagePageLoader } from './StorePage/StorePage.duck';
import { loadData as DealersDirectoryPageLoader } from './DealersDirectoryPage/DealersDirectoryPage.duck';
import { loadData as BrowseListingsPageLoader } from './BrowseListingsPage/BrowseListingsPage.duck';
import { loadData as BookmarkedListingsPageLoader } from './BookmarkedListingsPage/BookmarkedListingsPage.duck';
import { loadData as TeamPagePageLoader } from './TeamPage/TeamPage.duck';
import { loadData as SearchPageLoader } from './SearchPage/SearchPage.duck';
import { loadRecentlyViewedData as RecentlyViewedPageLoader } from './SearchPage/SearchPage.duck';
import { loadData as StripePayoutPageLoader } from './StripePayoutPage/StripePayoutPage.duck';
import { loadData as TermsOfServicePageLoader } from './TermsOfServicePage/TermsOfServicePage.duck';
import {
  loadData as TransactionPageLoader,
  setInitialValues as TransactionPageInitialValues,
} from './TransactionPage/TransactionPage.duck';
import { loadData as OnboardingPageLoader } from './OnboardingPage/OnboardingPage.duck';
import { parse, stringify } from '../util/urlHelpers';
import { PUBLIC_CATALOG_STOCK_FILTER } from '../util/publicCatalogStock';
// SellerOnboardingV2Page reuses the same loadData as OnboardingPage
import { loadData as SellerOnboardingV2PageLoader } from './OnboardingPage/OnboardingPage.duck';

const getPageDataLoadingAPI = () => {
  return {
    AuthenticationPage: {
      loadData: AuthenticationPageLoader,
    },
    LandingPage: {
      loadData: LandingPageLoader,
    },
    CheckoutPage: {
      setInitialValues: CheckoutPageInitialValues,
    },
    CMSPage: {
      loadData: CMSPageLoader,
    },
    ContactDetailsPage: {
      loadData: ContactDetailsPageLoader,
    },
    EditListingPage: {
      loadData: EditListingPageLoader,
    },
    EmailVerificationPage: {
      loadData: EmailVerificationPageLoader,
    },
    GroupPage: {
      loadData: GroupPageLoader,
    },
    GroupsPage: {
      loadData: GroupsPageLoader,
    },
    InboxPage: {
      loadData: InboxPageLoader,
    },
    ListingPage: {
      loadData: ListingPageLoader,
    },
    ManageListingsPage: {
      loadData: ManageListingsPageLoader,
    },
    PaymentMethodsPage: {
      loadData: PaymentMethodsPageLoader,
    },
    PrivacyPolicyPage: {
      loadData: PrivacyPolicyPageLoader,
    },
    ProfilePage: {
      loadData: ProfilePageLoader,
    },
    StorePage: {
      loadData: StorePagePageLoader,
    },
    DealersDirectoryPage: {
      loadData: DealersDirectoryPageLoader,
    },
    BrowseListingsPage: {
      loadData: BrowseListingsPageLoader,
    },
    BookmarkedListingsPage: {
      loadData: BookmarkedListingsPageLoader,
    },
    TeamPage: {
      loadData: (params, search, config) => (dispatch, getState, sdk) => {
        const parsedSearch = parse(search || '');
        const isWarehouseChannel = parsedSearch?.channel === 'warehouse';
        const isStoreDetailRoute = params?.type === 'detail';
        const shouldBypassSellerScope = isStoreDetailRoute && isWarehouseChannel;
        const enhancedSearchParams = {
          ...parsedSearch,
          ...(shouldBypassSellerScope ? {} : { author_id: params.id }),
        };
        const enhancedSearch = `?${stringify(enhancedSearchParams)}`;
        const currentTeamUserId = getState()?.TeamPage?.userId?.uuid;
        const shouldReloadTeamData = currentTeamUserId !== params.id;

        const calls = [
          dispatch(
            SearchPageLoader(params, enhancedSearch, config, {
              shouldFetchFullResults: false,
              useClientSidePagination: true,
              stockFilter: isStoreDetailRoute ? PUBLIC_CATALOG_STOCK_FILTER : null,
            })
          ).catch(error => {
            // Keep the seller page usable even if the embedded listing search fails.
            console.error('TeamPage embedded search load failed:', error);
            return null;
          }),
        ];

        // Team profile and reviews are stable for a given seller and don't need
        // to be re-fetched on every filter change inside the embedded search UI.
        if (shouldReloadTeamData) {
          calls.unshift(dispatch(TeamPagePageLoader(params, search, config)));
        }

        return Promise.all(calls);
      },
    },
    SearchPage: {
      loadData: SearchPageLoader,
    },
    RecentlyViewedPage: {
      loadData: RecentlyViewedPageLoader,
    },
    StripePayoutPage: {
      loadData: StripePayoutPageLoader,
    },
    TermsOfServicePage: {
      loadData: TermsOfServicePageLoader,
    },
    TransactionPage: {
      loadData: TransactionPageLoader,
      setInitialValues: TransactionPageInitialValues,
    },
    OnboardingPage: {
      loadData: OnboardingPageLoader,
    },
    SellerOnboardingV2Page: {
      loadData: SellerOnboardingV2PageLoader,
    },
  };
};

export default getPageDataLoadingAPI;
